import React, { useEffect, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { 
  Activity, Thermometer, AlertTriangle, Package, TrendingUp, 
  MapPin, Clock, Download, Bell as BellIcon, Share2, 
  Phone, Mail, MessageSquare, ChevronRight, ExternalLink
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Area, AreaChart } from 'recharts';
import { toast } from 'sonner';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { generateComplianceReport } from '@/utils/pdfReport';
import { LogoIcon } from '@/components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Norwegian cities
const NORWEGIAN_CITIES = {
  oslo: { lat: 59.9139, lon: 10.7522, name: 'Oslo' },
  stavanger: { lat: 58.9700, lon: 5.7331, name: 'Stavanger' },
  bergen: { lat: 60.3913, lon: 5.3221, name: 'Bergen' },
};

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [telemetryData, setTelemetryData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    tempMin: -20,
    tempMax: 8,
    locationAlerts: true,
    email: true,
    sms: false,
    webhook: false,
    webhookUrl: '',
  });

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['stats'],
    queryFn: async () => {
      const response = await axios.get(`${API}/stats`);
      return response.data;
    },
    refetchInterval: 10000,
  });

  // Fetch devices
  const { data: devices = [] } = useQuery({
    queryKey: ['devices'],
    queryFn: async () => {
      const response = await axios.get(`${API}/devices`);
      return response.data;
    },
    refetchInterval: 5000,
  });

  // Fetch shipments
  const { data: shipments = [] } = useQuery({
    queryKey: ['shipments'],
    queryFn: async () => {
      const response = await axios.get(`${API}/shipments`);
      return response.data;
    },
    refetchInterval: 5000,
  });

  // Fetch recent telemetry
  useEffect(() => {
    const fetchTelemetry = async () => {
      try {
        const response = await axios.get(`${API}/telemetry?limit=100`);
        setTelemetryData(response.data);
      } catch {
        // Telemetry fetch is non-critical, silently retry
      }
    };
    fetchTelemetry();
    const interval = setInterval(fetchTelemetry, 5000);
    return () => clearInterval(interval);
  }, []); // API is a module-level constant

  // Generate simulated temperature history for chart
  const temperatureHistory = useMemo(() => {
    const hours = 24;
    const data = [];
    const now = new Date();
    // Use a seeded pseudo-random function for consistency
    const seededRandom = (seed) => {
      const x = Math.sin(seed) * 10000;
      return x - Math.floor(x);
    };
    for (let i = hours; i >= 0; i--) {
      const time = new Date(now - i * 3600000);
      const baseTemp = -2;
      const variation = Math.sin(i / 4) * 3 + (seededRandom(i * 17) - 0.5) * 2;
      data.push({
        time: time.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
        temp: Math.round((baseTemp + variation) * 10) / 10,
        min: -20,
        max: 8,
      });
    }
    return data;
  }, []);

  // Stats cards configuration
  const statsCards = [
    {
      label: user?.role === 'pallet_pool' ? 'TOTAL DEVICES' : 'ACTIVE SHIPMENTS',
      value: user?.role === 'pallet_pool' ? (stats?.total_devices || 0) : (stats?.active_shipments || 0),
      change: '+12%',
      changeLabel: 'from yesterday',
      icon: Package,
      color: 'from-blue-600 to-cyan-500',
      bgColor: 'bg-blue-100',
      textColor: 'text-blue-600',
    },
    {
      label: 'IN TRANSIT',
      value: stats?.active_shipments || 0,
      change: '+8%',
      changeLabel: 'today',
      icon: Activity,
      color: 'from-purple-600 to-pink-500',
      bgColor: 'bg-purple-100',
      textColor: 'text-purple-600',
    },
    {
      label: 'DELIVERED TODAY',
      value: stats?.total_shipments ? Math.floor(stats.total_shipments * 0.3) : 0,
      change: '+5',
      changeLabel: 'since 2PM',
      icon: Package,
      color: 'from-green-600 to-emerald-500',
      bgColor: 'bg-green-100',
      textColor: 'text-green-600',
    },
    {
      label: 'ACTIVE ALERTS',
      value: stats?.total_alerts || 0,
      change: stats?.recent_events > 0 ? `${stats?.recent_events}` : '0',
      changeLabel: 'new today',
      icon: AlertTriangle,
      color: 'from-orange-600 to-red-500',
      bgColor: 'bg-orange-100',
      textColor: 'text-orange-600',
    },
  ];

  // Get shipment status info with visual indicators
  const getStatusInfo = (shipment, telemetry) => {
    const temp = telemetry?.temperature;
    let status = 'on-track';
    let label = 'On Track';
    let color = 'bg-green-500';
    let bgColor = 'bg-green-100';
    let textColor = 'text-green-700';
    let indicator = '🟢';

    if (temp !== undefined) {
      if (temp > 8 && temp <= 12) {
        status = 'warning';
        label = 'Minor Delay';
        color = 'bg-yellow-500';
        bgColor = 'bg-yellow-100';
        textColor = 'text-yellow-700';
        indicator = '🟡';
      } else if (temp > 12 || temp < -25) {
        status = 'alert';
        label = 'Temp Alert';
        color = 'bg-red-500';
        bgColor = 'bg-red-100';
        textColor = 'text-red-700';
        indicator = '🔴';
      }
    }

    return { status, label, color, bgColor, textColor, indicator };
  };

  const getStatusBadge = (status) => {
    const badges = {
      created: { label: 'Created', className: 'bg-slate-100 text-slate-700', dot: 'bg-slate-500' },
      activated: { label: 'Active', className: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
      in_transit: { label: 'In Transit', className: 'bg-purple-100 text-purple-700', dot: 'bg-purple-500' },
      delivered: { label: 'Delivered', className: 'bg-orange-100 text-orange-700', dot: 'bg-orange-500' },
      accepted: { label: 'Accepted', className: 'bg-green-100 text-green-700', dot: 'bg-green-500' },
      rejected: { label: 'Rejected', className: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
    };
    return badges[status] || badges.created;
  };

  const getTempClass = (temp) => {
    if (temp < -15) return 'text-blue-600 font-bold';
    if (temp >= -15 && temp <= 8) return 'text-green-600 font-bold';
    if (temp > 8 && temp <= 12) return 'text-orange-600 font-bold';
    return 'text-red-600 font-bold';
  };

  const filteredShipments = shipments.filter((s) =>
    s.sscc.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.origin.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.destination.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleViewDetails = (shipment) => {
    setSelectedShipment(shipment);
    setIsModalOpen(true);
  };

  const handleExportReport = (shipment = selectedShipment) => {
    if (!shipment) {
      toast.error('No shipment selected');
      return;
    }
    
    toast.info('Generating PDF report...');
    
    try {
      // Get temperature data for this shipment
      const tempData = telemetryData
        .filter(t => t.device_id === shipment.device_id)
        .map(t => ({
          time: t.timestamp,
          temperature: t.temperature
        }));
      
      const filename = generateComplianceReport(shipment, tempData);
      toast.success(`Report downloaded: ${filename}`);
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate report');
    }
  };

  const handleShareLink = () => {
    const link = `${window.location.origin}/track/${selectedShipment?.sscc}`;
    navigator.clipboard.writeText(link);
    toast.success('Tracking link copied to clipboard!');
  };

  const handleSaveAlertConfig = () => {
    toast.success('Alert configuration saved!');
    setIsAlertDialogOpen(false);
  };

  // Get device location for map — use real coordinates from device/shipment data
  const getDeviceLocation = (deviceId) => {
    // Check if there's an active shipment with this device
    const shipment = shipments.find(s => s.device_id === deviceId);
    if (shipment?.current_latitude && shipment?.current_longitude) {
      return { lat: shipment.current_latitude, lon: shipment.current_longitude, city: shipment.origin || deviceId };
    }
    // Fall back to device last known location
    const device = devices.find(d => d.device_id === deviceId);
    if (device?.last_latitude && device?.last_longitude) {
      return { lat: device.last_latitude, lon: device.last_longitude, city: device.name || deviceId };
    }
    return null;
  };

  // Get marker icon
  const getMarkerIcon = (color) => {
    return L.divIcon({
      className: 'custom-marker',
      html: `<div style="background:${color};width:24px;height:24px;border-radius:50%;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);"></div>`,
      iconSize: [24, 24],
      iconAnchor: [12, 12],
    });
  };

  return (
    <div className="space-y-0" data-testid="dashboard">
      {/* Header with Search */}
      <div className="bg-white border-b border-slate-200 px-8 py-5 flex items-center justify-between">
        <div className="flex-1 max-w-xl">
          <div className="relative">
            <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search by SSCC or destination..."
              className="w-full pl-12 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:border-blue-500 transition-colors"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="ml-4">
          <div className="flex items-center gap-3 px-4 py-2 bg-slate-50 rounded-xl">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center text-white font-bold text-sm">
              {user?.name?.split(' ').map(n => n[0]).join('') || 'U'}
            </div>
            <div className="text-sm font-semibold text-slate-900">{user?.name}</div>
          </div>
        </div>
      </div>

      {/* Stats Grid - Glassmorphism */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 p-8 bg-gradient-to-br from-slate-50/50 to-blue-50/50">
        {statsCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.label} className="bg-white/60 backdrop-blur-md border border-white/30 shadow-lg shadow-blue-500/5 hover:shadow-xl hover:bg-white/80 transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="text-xs font-bold text-slate-500 tracking-wider">{stat.label}</div>
                  <div className={`w-12 h-12 rounded-xl ${stat.bgColor} backdrop-blur-sm flex items-center justify-center`}>
                    <Icon className={`w-6 h-6 ${stat.textColor}`} />
                  </div>
                </div>
                <div className="font-mono text-4xl font-bold text-slate-900 mb-2">{stat.value}</div>
                <div className="flex items-center gap-1 text-sm">
                  <TrendingUp className="w-4 h-4 text-green-600" />
                  <span className="text-green-600 font-semibold">{stat.change}</span>
                  <span className="text-slate-500">{stat.changeLabel}</span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Live Map Widget */}
      <div className="px-8 pb-4">
        <Card className="overflow-hidden">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Live Shipment Map</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => window.location.href = '/app/map'}>
                View Full Map <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div style={{ height: '300px', width: '100%' }}>
              <MapContainer
                center={(() => {
                  const locs = devices.map(d => getDeviceLocation(d.device_id)).filter(Boolean);
                  if (locs.length > 0) return [locs[0].lat, locs[0].lon];
                  return [59.9, 10.7];
                })()}
                zoom={6}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={false}
              >
                <TileLayer
                  attribution='&copy; OpenStreetMap'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MarkerClusterGroup
                  chunkedLoading
                  maxClusterRadius={50}
                  spiderfyOnMaxZoom={true}
                  showCoverageOnHover={false}
                  iconCreateFunction={(cluster) => {
                    const count = cluster.getChildCount();
                    return L.divIcon({
                      html: `<div style="
                        background: linear-gradient(135deg, #2563eb, #06b6d4);
                        color: white;
                        border-radius: 50%;
                        width: 40px;
                        height: 40px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-weight: 700;
                        font-size: 14px;
                        border: 3px solid white;
                        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                      ">${count}</div>`,
                      className: '',
                      iconSize: L.point(40, 40),
                      iconAnchor: L.point(20, 20),
                    });
                  }}
                >
                {devices.map((device) => {
                  const location = getDeviceLocation(device.device_id);
                  if (!location) return null;
                  const telemetry = telemetryData.find(t => t.device_id === device.device_id);
                  const temp = telemetry?.temperature;
                  let color = '#10b981';
                  if (temp > 8 && temp <= 12) color = '#f59e0b';
                  if (temp > 12) color = '#ef4444';
                  
                  return (
                    <Marker
                      key={device.device_id}
                      position={[location.lat, location.lon]}
                      icon={getMarkerIcon(color)}
                    >
                      <Popup>
                        <div className="text-sm">
                          <div className="font-bold">{device.name}</div>
                          <div>{location.city}</div>
                          {telemetry && <div className="font-mono">{telemetry.temperature?.toFixed(1)}°C</div>}
                          {device.battery_mv && <div className="font-mono">{device.battery_mv}mV (~{device.battery_level}%)</div>}
                        </div>
                      </Popup>
                    </Marker>
                  );
                })}
                </MarkerClusterGroup>
              </MapContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Shipments Table - Glassmorphism */}
      <div className="px-8 pb-8">
        <div className="bg-white/70 backdrop-blur-md rounded-2xl border border-white/30 shadow-xl shadow-blue-500/5 overflow-hidden">
          <div className="px-6 py-5 border-b border-white/30 flex items-center justify-between bg-white/40">
            <h2 className="text-xl font-bold text-slate-900">Active Shipments</h2>
            <div className="flex gap-3">
              <Button variant="outline" size="sm" className="bg-white/50 backdrop-blur-sm border-white/30 hover:bg-white/80">
                <Activity className="w-4 h-4 mr-2" />
                Filter
              </Button>
              <Button size="sm" className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 shadow-md"
                onClick={() => navigate('/app/shipments')} data-testid="new-shipment-btn">
                <Package className="w-4 h-4 mr-2" />
                New Shipment
              </Button>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">SSCC</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Route</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Temperature</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">ETA</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredShipments.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-slate-500">
                      No shipments found
                    </td>
                  </tr>
                ) : (
                  filteredShipments.map((shipment) => {
                    const latestTemp = telemetryData.find(t => t.device_id === shipment.device_id);
                    const statusInfo = getStatusInfo(shipment, latestTemp);
                    
                    return (
                      <tr key={shipment.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <span className={`w-3 h-3 rounded-full ${statusInfo.color} animate-status-pulse`}></span>
                            <span className={`text-sm font-medium ${statusInfo.textColor}`}>{statusInfo.label}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <code className="text-sm font-mono font-semibold text-slate-900 bg-slate-100 px-3 py-1 rounded-md">
                            {shipment.sscc}
                          </code>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-900">
                          {shipment.origin} → {shipment.destination}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`font-mono text-sm ${latestTemp ? getTempClass(latestTemp.temperature) : 'text-slate-400'}`}>
                            {latestTemp ? `${latestTemp.temperature.toFixed(1)}°C` : 'N/A'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            ~2h 30m
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleViewDetails(shipment)}
                              className="hover:border-blue-500 hover:text-blue-600"
                            >
                              View Details
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => { setSelectedShipment(shipment); setIsAlertDialogOpen(true); }}
                              className="hover:text-orange-600"
                            >
                              <BellIcon className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={handleExportReport}
                              className="hover:text-green-600"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Shipment Details Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl flex items-center gap-3">
              Shipment Details
              {selectedShipment && (
                <Badge className="bg-blue-100 text-blue-700">
                  {selectedShipment.status?.replace('_', ' ')}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedShipment && (
            <div className="space-y-6">
              {/* Quick Info Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-slate-50 p-4 rounded-lg border-l-4 border-blue-500">
                  <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">SSCC</div>
                  <div className="font-mono font-semibold">{selectedShipment.sscc}</div>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg border-l-4 border-green-500">
                  <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Origin</div>
                  <div className="font-semibold">{selectedShipment.origin}</div>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg border-l-4 border-purple-500">
                  <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Destination</div>
                  <div className="font-semibold">{selectedShipment.destination}</div>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg border-l-4 border-orange-500">
                  <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Temp Range</div>
                  <div className="font-mono font-semibold">{selectedShipment.expected_temp_min}°C - {selectedShipment.expected_temp_max}°C</div>
                </div>
              </div>

              {/* Two Column Layout */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Temperature Chart */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Thermometer className="w-5 h-5 text-blue-600" />
                      Temperature History (24h)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={temperatureHistory}>
                          <defs>
                            <linearGradient id="tempGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="time" tick={{ fontSize: 10 }} stroke="#94a3b8" />
                          <YAxis domain={[-25, 15]} tick={{ fontSize: 10 }} stroke="#94a3b8" />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'white', 
                              border: '1px solid #e2e8f0',
                              borderRadius: '8px',
                              boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                            }}
                          />
                          <ReferenceLine y={8} stroke="#f59e0b" strokeDasharray="5 5" label={{ value: 'Max', fill: '#f59e0b', fontSize: 10 }} />
                          <ReferenceLine y={-20} stroke="#3b82f6" strokeDasharray="5 5" label={{ value: 'Min', fill: '#3b82f6', fontSize: 10 }} />
                          <Area type="monotone" dataKey="temp" stroke="#3b82f6" fill="url(#tempGradient)" strokeWidth={2} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Current Location Map */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-green-600" />
                      Current Location
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div style={{ height: '200px', width: '100%' }}>
                      {selectedShipment.current_latitude && selectedShipment.current_longitude ? (
                        <MapContainer
                          center={[selectedShipment.current_latitude, selectedShipment.current_longitude]}
                          zoom={10}
                          style={{ height: '100%', width: '100%', borderRadius: '0 0 8px 8px' }}
                          scrollWheelZoom={false}
                        >
                          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                          <Marker 
                            position={[selectedShipment.current_latitude, selectedShipment.current_longitude]} 
                            icon={getMarkerIcon('#3b82f6')}
                          >
                            <Popup>
                              <div className="text-sm">
                                <div className="font-bold">{selectedShipment.sscc}</div>
                                <div>{selectedShipment.current_latitude.toFixed(4)}, {selectedShipment.current_longitude.toFixed(4)}</div>
                                {selectedShipment.last_location_update && (
                                  <div className="text-xs text-slate-500">
                                    Updated: {new Date(selectedShipment.last_location_update).toLocaleString()}
                                  </div>
                                )}
                              </div>
                            </Popup>
                          </Marker>
                        </MapContainer>
                      ) : (
                        <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                          No location data available
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Timeline */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Transport Timeline</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    {/* Created */}
                    <div className="flex flex-col items-center">
                      <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center text-white mb-2">
                        <Package className="w-5 h-5" />
                      </div>
                      <div className="text-xs font-bold">Loaded</div>
                      <div className="text-xs text-slate-500">
                        {new Date(selectedShipment.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="flex-1 h-1 bg-green-500 mx-2"></div>
                    
                    {/* Departed */}
                    <div className="flex flex-col items-center">
                      <div className={`w-10 h-10 rounded-full ${selectedShipment.activated_at ? 'bg-blue-500' : 'bg-slate-300'} flex items-center justify-center text-white mb-2`}>
                        <Activity className="w-5 h-5" />
                      </div>
                      <div className="text-xs font-bold">Departed</div>
                      <div className="text-xs text-slate-500">
                        {selectedShipment.activated_at ? new Date(selectedShipment.activated_at).toLocaleDateString() : 'Pending'}
                      </div>
                    </div>
                    <div className={`flex-1 h-1 ${selectedShipment.activated_at ? 'bg-blue-500' : 'bg-slate-200'} mx-2`}></div>
                    
                    {/* In Transit */}
                    <div className="flex flex-col items-center">
                      <div className={`w-10 h-10 rounded-full ${selectedShipment.status === 'in_transit' ? 'bg-purple-500 animate-pulse' : 'bg-slate-300'} flex items-center justify-center text-white mb-2`}>
                        <MapPin className="w-5 h-5" />
                      </div>
                      <div className="text-xs font-bold">In Transit</div>
                      <div className="text-xs text-slate-500">Now</div>
                    </div>
                    <div className={`flex-1 h-1 ${selectedShipment.delivered_at ? 'bg-orange-500' : 'bg-slate-200'} mx-2`}></div>
                    
                    {/* Delivered */}
                    <div className="flex flex-col items-center">
                      <div className={`w-10 h-10 rounded-full ${selectedShipment.delivered_at ? 'bg-orange-500' : 'bg-slate-300'} flex items-center justify-center text-white mb-2`}>
                        <Clock className="w-5 h-5" />
                      </div>
                      <div className="text-xs font-bold">Delivered</div>
                      <div className="text-xs text-slate-500">
                        {selectedShipment.delivered_at ? new Date(selectedShipment.delivered_at).toLocaleDateString() : 'ETA ~2h'}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Alert Panel */}
              <Card className="border-orange-200 bg-orange-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2 text-orange-700">
                    <AlertTriangle className="w-5 h-5" />
                    Active Alerts
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-orange-200">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                        <div>
                          <div className="font-medium text-sm">Temperature approaching threshold</div>
                          <div className="text-xs text-slate-500">10 minutes ago</div>
                        </div>
                      </div>
                      <Badge className="bg-yellow-100 text-yellow-700">Warning</Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-green-200">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-green-500"></div>
                        <div>
                          <div className="font-medium text-sm">Shipment on schedule</div>
                          <div className="text-xs text-slate-500">30 minutes ago</div>
                        </div>
                      </div>
                      <Badge className="bg-green-100 text-green-700">Info</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Actions */}
              <div className="flex flex-wrap gap-3 pt-4 border-t border-slate-200">
                <Button variant="outline" onClick={handleShareLink}>
                  <Share2 className="w-4 h-4 mr-2" />
                  Share Tracking Link
                </Button>
                <Button variant="outline" onClick={handleExportReport}>
                  <Download className="w-4 h-4 mr-2" />
                  Export Compliance Report
                </Button>
                <Button variant="outline" onClick={() => { setIsModalOpen(false); setIsAlertDialogOpen(true); }}>
                  <BellIcon className="w-4 h-4 mr-2" />
                  Configure Alerts
                </Button>
                <Button variant="outline">
                  <Phone className="w-4 h-4 mr-2" />
                  Contact Support
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Alert Configuration Dialog */}
      <Dialog open={isAlertDialogOpen} onOpenChange={setIsAlertDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BellIcon className="w-5 h-5 text-orange-600" />
              Alert Configuration
            </DialogTitle>
            <DialogDescription>
              Set up notification rules for this shipment
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Temperature Thresholds */}
            <div className="space-y-4">
              <Label className="text-sm font-bold">Temperature Thresholds</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="tempMin" className="text-xs text-slate-500">Min (°C)</Label>
                  <Input
                    id="tempMin"
                    type="number"
                    value={alertConfig.tempMin}
                    onChange={(e) => setAlertConfig(prev => ({ ...prev, tempMin: parseFloat(e.target.value) }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tempMax" className="text-xs text-slate-500">Max (°C)</Label>
                  <Input
                    id="tempMax"
                    type="number"
                    value={alertConfig.tempMax}
                    onChange={(e) => setAlertConfig(prev => ({ ...prev, tempMax: parseFloat(e.target.value) }))}
                  />
                </div>
              </div>
            </div>

            {/* Alert Types */}
            <div className="space-y-3">
              <Label className="text-sm font-bold">Alert Types</Label>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-slate-500" />
                  <span className="text-sm">Location alerts</span>
                </div>
                <Switch
                  checked={alertConfig.locationAlerts}
                  onCheckedChange={(checked) => setAlertConfig(prev => ({ ...prev, locationAlerts: checked }))}
                />
              </div>
            </div>

            {/* Notification Channels */}
            <div className="space-y-3">
              <Label className="text-sm font-bold">Notification Channels</Label>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-slate-500" />
                  <span className="text-sm">Email</span>
                </div>
                <Switch
                  checked={alertConfig.email}
                  onCheckedChange={(checked) => setAlertConfig(prev => ({ ...prev, email: checked }))}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-slate-500" />
                  <span className="text-sm">SMS</span>
                </div>
                <Switch
                  checked={alertConfig.sms}
                  onCheckedChange={(checked) => setAlertConfig(prev => ({ ...prev, sms: checked }))}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ExternalLink className="w-4 h-4 text-slate-500" />
                  <span className="text-sm">Webhook</span>
                </div>
                <Switch
                  checked={alertConfig.webhook}
                  onCheckedChange={(checked) => setAlertConfig(prev => ({ ...prev, webhook: checked }))}
                />
              </div>
              
              {alertConfig.webhook && (
                <Input
                  placeholder="https://your-webhook-url.com/alerts"
                  value={alertConfig.webhookUrl}
                  onChange={(e) => setAlertConfig(prev => ({ ...prev, webhookUrl: e.target.value }))}
                  className="mt-2"
                />
              )}
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setIsAlertDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleSaveAlertConfig} className="flex-1 bg-gradient-to-r from-blue-600 to-cyan-500">
                Save Configuration
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="mt-8 px-8 pb-8">
        <div className="bg-white/60 backdrop-blur-md rounded-2xl border border-white/20 shadow-lg p-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <LogoIcon size={32} />
              <div>
                <p className="text-sm font-semibold text-slate-900">SmartPallet</p>
                <p className="text-xs text-slate-500">An Invig Product</p>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-slate-600">
              <a href="mailto:support@invig.no" className="flex items-center gap-2 hover:text-blue-600 transition-colors">
                <Mail className="w-4 h-4" />
                support@invig.no
              </a>
              <a href="tel:+4740066686" className="flex items-center gap-2 hover:text-blue-600 transition-colors">
                <Phone className="w-4 h-4" />
                +47 400 66 686
              </a>
              <a href="mailto:daniel@invig.no" className="flex items-center gap-2 hover:text-blue-600 transition-colors">
                <MessageSquare className="w-4 h-4" />
                Contact Sales
              </a>
            </div>
            
            <div className="text-xs text-slate-500 text-center md:text-right">
              <p>© 2025 Invig AS. All rights reserved.</p>
              <p className="mt-1">
                <a href="https://invig.no" target="_blank" rel="noopener noreferrer" className="hover:text-blue-600">
                  invig.no
                </a>
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Dashboard;
